import { DialogComponent } from '@theme/dialog';

/**
 * Shipping & Returns slide-over panel.
 * Shares delivery location + availability with the PDP shipping module via
 * window.SaboDelivery and the sabo:delivery-location-changed /
 * sabo:availability-updated document events. Only fetches availability itself
 * when no PDP shipping module is present on the page (avoids double fetching).
 * localStorage key: sabo_delivery_location
 */

const STORAGE_KEY = 'sabo_delivery_location';
const LOCATION_EVENT = 'sabo:delivery-location-changed';
const AVAILABILITY_EVENT = 'sabo:availability-updated';
const FETCH_TIMEOUT_MS = 3000;

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BRISBANE_OFFSET_MS = 10 * 60 * 60 * 1000;

function sharedStore() {
  if (!window.SaboDelivery) window.SaboDelivery = { location: null, availabilityByProduct: {} };
  if (!window.SaboDelivery.availabilityByProduct) window.SaboDelivery.availabilityByProduct = {};
  return window.SaboDelivery;
}

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.postcode) return null;
    return { postcode: String(data.postcode).trim(), suburb: data.suburb ? String(data.suburb).trim() : '' };
  } catch (_e) {
    return null;
  }
}

function writeStorage(postcode, suburb, state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        postcode: String(postcode || '').trim(),
        suburb: String(suburb || '').trim(),
        state: String(state || '').trim(),
        fetchedAt: Date.now(),
      })
    );
  } catch (_e) {
    /* ignore */
  }
}

function padPostcode(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.slice(0, 4).padStart(4, '0');
}

function titleCaseSuburb(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function brisbaneNowParts() {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const map = {};
  parts.forEach((p) => (map[p.type] = p.value));
  return { year: parseInt(map.year, 10), month: parseInt(map.month, 10), day: parseInt(map.day, 10) };
}

function brisbaneDateFromParts(parts) {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day) - BRISBANE_OFFSET_MS);
}

function shiftedParts(date) {
  const shifted = new Date(date.getTime() + BRISBANE_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    weekday: shifted.getUTCDay(),
  };
}

function addBusinessDays(parts, days) {
  let d = brisbaneDateFromParts(parts);
  let left = Math.max(0, days | 0);
  while (left > 0) {
    d = new Date(d.getTime() + 24 * 60 * 60 * 1000);
    const wd = new Date(d.getTime() + BRISBANE_OFFSET_MS).getUTCDay();
    if (wd !== 0 && wd !== 6) left -= 1;
  }
  return shiftedParts(d);
}

function addCalendarDays(parts, days) {
  const d = new Date(brisbaneDateFromParts(parts).getTime() + days * 24 * 60 * 60 * 1000);
  return shiftedParts(d);
}

function formatArrivesRange(minDays, maxDays) {
  const now = brisbaneNowParts();
  const start = addBusinessDays(now, minDays);
  const end = addBusinessDays(now, maxDays);
  const sameMonth = start.month === end.month;
  let left = WEEKDAY_SHORT[start.weekday] + ' ' + start.day;
  const right = WEEKDAY_SHORT[end.weekday] + ' ' + end.day + ' ' + MONTH_SHORT[end.month - 1];
  if (!sameMonth) left += ' ' + MONTH_SHORT[start.month - 1];
  return 'Arrives ' + left + ' – ' + right;
}

function parseIsoDateParts(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  return { year, month, day, weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay() };
}

function compactDayLabel(dateIso) {
  const parts = parseIsoDateParts(dateIso);
  if (!parts) return '';
  const now = brisbaneNowParts();
  if (parts.year === now.year && parts.month === now.month && parts.day === now.day) return 'Today';
  const tomorrow = addCalendarDays(now, 1);
  if (parts.year === tomorrow.year && parts.month === tomorrow.month && parts.day === tomorrow.day) return 'Tomorrow';
  return WEEKDAY_SHORT[parts.weekday] + ' ' + parts.day + ' ' + MONTH_SHORT[parts.month - 1];
}

function isEveningWindow(win) {
  const code = String((win && win.code) || '').toUpperCase();
  const slot = String((win && win.window) || '').toLowerCase();
  return code.indexOf('TON') >= 0 || slot.indexOf('5-9') >= 0 || slot.indexOf('5–9') >= 0;
}

function windowDisplayName(win) {
  if (win && win.displayName) return String(win.displayName);
  if (win && win.name) return String(win.name);
  const code = String((win && win.code) || '').toUpperCase();
  if (code.indexOf('TON') >= 0) return 'Same Night';
  if (code.indexOf('AFT') >= 0) return 'Same Day';
  return isEveningWindow(win) ? 'Same Night' : 'Same Day';
}

function formatWindowRange(win) {
  const slug = String((win && win.window) || '')
    .toLowerCase()
    .replace(/–/g, '-')
    .replace(/\s+/g, '');
  if (slug.indexOf('5-9') >= 0) return '5–9 PM';
  if (slug.indexOf('1-5') >= 0) return '1–5 PM';
  const label = String((win && win.label) || '');
  const match = label.match(/(\d+)\s*[–-]\s*(\d+)\s*(AM|PM)/i);
  if (match) return match[1] + '–' + match[2] + ' ' + match[3].toUpperCase();
  return isEveningWindow(win) ? '5–9 PM' : '1–5 PM';
}

function windowEndLabel(win) {
  const range = formatWindowRange(win);
  const m = range.match(/(\d+)\s*(AM|PM)\s*$/i);
  return m ? m[1] + ' ' + m[2].toUpperCase() : range;
}

function windowDeliveryPhrase(win) {
  const day = compactDayLabel(win && win.date);
  const evening = isEveningWindow(win);
  const range = formatWindowRange(win);
  const end = windowEndLabel(win);
  if (day === 'Today') return evening ? 'Tonight ' + range : 'Today by ' + end;
  return day ? day + ' ' + range : range;
}

class ShippingReturnsPanel extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    this.productId = this.getAttribute('data-product-id') || '';
    this.availabilityUrl = (this.getAttribute('data-availability-url') || '').trim();
    this.sameDayPrice = this.getAttribute('data-same-day-price') || '$14.95';
    this.variantId = this.getAttribute('data-variant-id') || '';
    this.changeLabel = this.getAttribute('data-change-label') || 'Change';

    this.locLine = this.querySelector('[data-sr-loc-line]');
    this.locAction = this.querySelector('[data-sr-loc-action]');
    this.inlineCheck = this.querySelector('[data-sr-inline-check]');
    this.postcodeInput = this.querySelector('[data-sr-postcode]');
    this.checkBtn = this.querySelector('[data-sr-check]');
    this.windowRows = Array.prototype.slice.call(this.querySelectorAll('[data-sr-window-row]'));
    this.sameDayCheckRow = this.querySelector('[data-sr-sameday-check]');
    this.noteEl = this.querySelector('[data-sr-note]');

    this.location = readStorage() || sharedStore().location || null;
    this.opened = false;

    this.fillStaticArrivals();
    this.bindPanel();
    this.renderLocation();
    this.renderHomeDelivery(this.cachedAvailability());
  }

  cachedAvailability() {
    const store = sharedStore();
    return (this.productId && store.availabilityByProduct[this.productId]) || null;
  }

  moduleAbsent() {
    return !document.querySelector('[data-pdp-shipping]');
  }

  fillStaticArrivals() {
    const map = { standard: ['data-standard-min', 'data-standard-max'], express: ['data-express-min', 'data-express-max'] };
    this.querySelectorAll('[data-sr-static]').forEach((row) => {
      const key = row.getAttribute('data-sr-static');
      const attrs = map[key];
      if (!attrs) return;
      const min = parseInt(this.getAttribute(attrs[0]) || '0', 10);
      const max = parseInt(this.getAttribute(attrs[1]) || '0', 10);
      const el = row.querySelector('[data-sr-arrival]');
      if (!el || Number.isNaN(min) || Number.isNaN(max)) return;
      let text = min + ' to ' + max + ' business days';
      if (key === 'standard') text += ', free over $100';
      el.textContent = text;
    });
  }

  bindPanel() {
    this.addEventListener('dialog:close', () => {
      this.opened = false;
      if (this.invoker && typeof this.invoker.focus === 'function') this.invoker.focus();
    });

    if (this.locAction) this.locAction.addEventListener('click', () => this.showInlineCheck());
    if (this.checkBtn) this.checkBtn.addEventListener('click', () => this.submitLocation());
    if (this.postcodeInput) {
      this.postcodeInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.submitLocation();
        }
      });
    }

    document.addEventListener(AVAILABILITY_EVENT, (event) => {
      const detail = (event && event.detail) || {};
      if (detail.productId && this.productId && String(detail.productId) !== String(this.productId)) return;
      this.renderHomeDelivery(detail.data || null);
    });

    document.addEventListener(LOCATION_EVENT, (event) => {
      const detail = (event && event.detail) || {};
      this.location = detail.postcode ? { postcode: padPostcode(detail.postcode), suburb: detail.suburb || '' } : null;
      this.renderLocation();
      if (!this.location) this.renderHomeDelivery(null);
    });

    // Keep the panel's reused <store-stock> in sync with variant changes.
    // The store-stock component listens on its closest section OR dialog, but
    // variant:update bubbles up from the variant picker and never propagates
    // INTO the dialog subtree — so forward it onto the dialog directly.
    document.addEventListener('variant:update', (event) => {
      const resource = event && event.detail && event.detail.resource;
      if (resource && resource.id) this.variantId = String(resource.id);
      this.forwardVariantToStoreStock(event);
    });

    document.addEventListener('sabo:open-shipping-panel', (event) => {
      this.open(event && event.detail && event.detail.trigger);
    });

    document.addEventListener('click', (event) => {
      const trigger =
        event.target instanceof Element ? event.target.closest('[data-shipping-returns-trigger]') : null;
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  /** Public entry point used by the PDP module link and product-details trigger. */
  open(trigger) {
    this.invoker = trigger || null;
    this.showDialog();
    this.opened = true;
    this.location = readStorage() || sharedStore().location || null;
    this.renderLocation();
    this.renderHomeDelivery(this.cachedAvailability());
    this.maybeScrollTo(trigger);
  }

  /**
   * Optionally scroll a section into view once the drawer is open. The invoking
   * trigger can carry data-sr-scroll (e.g. the "Find in store" row → 'store').
   * @param {Element|null} trigger
   */
  maybeScrollTo(trigger) {
    const key = trigger && trigger.getAttribute ? trigger.getAttribute('data-sr-scroll') : '';
    if (!key) return;
    const map = { store: '.sr-panel__section--stores', stores: '.sr-panel__section--stores' };
    const sel = map[key];
    if (!sel) return;
    const target = this.querySelector(sel);
    if (!target || typeof target.scrollIntoView !== 'function') return;
    // Wait for the drawer's slide-in before scrolling so the offset is correct.
    setTimeout(() => {
      try {
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } catch (_e) {
        target.scrollIntoView();
      }
    }, 380);
  }

  /**
   * Re-dispatch a variant:update onto the dialog so the in-panel <store-stock>
   * (which scopes its listener to the closest dialog) refetches. Dispatched
   * non-bubbling on the dialog to avoid re-triggering the document listener.
   */
  forwardVariantToStoreStock(event) {
    const dialog = this.refs && this.refs.dialog;
    const detail = event && event.detail;
    if (!dialog || !detail || !detail.resource || !detail.resource.id) return;
    dialog.dispatchEvent(new CustomEvent('variant:update', { detail }));
  }

  showInlineCheck() {
    if (!this.inlineCheck) return;
    this.inlineCheck.hidden = false;
    if (this.postcodeInput) {
      if (this.location && this.location.postcode) this.postcodeInput.value = this.location.postcode;
      this.postcodeInput.focus();
      this.postcodeInput.select();
    }
  }

  submitLocation() {
    const pc = padPostcode(this.postcodeInput ? this.postcodeInput.value : '');
    if (!pc || pc.length < 3) {
      if (this.postcodeInput) this.postcodeInput.focus();
      return;
    }
    this.location = { postcode: pc, suburb: '' };
    writeStorage(pc, '');
    sharedStore().location = { postcode: pc, suburb: '' };
    if (this.inlineCheck) this.inlineCheck.hidden = true;
    this.renderLocation();
    document.dispatchEvent(
      new CustomEvent(LOCATION_EVENT, { detail: { postcode: pc, suburb: '', source: 'sr-panel' } })
    );
    if (this.moduleAbsent()) this.fetchAvailability(pc, '');
  }

  renderLocation() {
    if (!this.locLine) return;
    this.locLine.innerHTML = '';
    if (this.location && this.location.postcode) {
      this.locLine.appendChild(document.createTextNode('Delivering to '));
      const pc = document.createElement('span');
      pc.className = 'sr-panel__loc-strong';
      pc.textContent = this.location.postcode;
      this.locLine.appendChild(pc);
      const suburb = titleCaseSuburb(this.location.suburb);
      if (suburb) {
        this.locLine.appendChild(document.createTextNode(' '));
        const sub = document.createElement('span');
        sub.className = 'sr-panel__loc-strong sr-panel__loc-strong--suburb';
        sub.textContent = suburb;
        this.locLine.appendChild(sub);
      }
      if (this.locAction) this.locAction.textContent = this.changeLabel;
    } else {
      this.locLine.textContent = 'Add your postcode for local delivery times';
      if (this.locAction) this.locAction.textContent = 'Add';
    }
  }

  renderHomeDelivery(data) {
    const windows = (data && Array.isArray(data.windows) && data.windows) || [];
    const state = String((data && data.state) || '').toLowerCase();
    const resolved = !!(this.location && this.location.postcode);
    const showWindows = windows.length > 0 && state !== 'not_serviced';

    this.windowRows.forEach((row, index) => {
      const win = showWindows ? windows[index] : null;
      if (!win) {
        row.hidden = true;
        return;
      }
      row.hidden = false;
      const nameEl = row.querySelector('[data-sr-window-name]');
      const detailEl = row.querySelector('[data-sr-window-detail]');
      const priceEl = row.querySelector('[data-sr-window-price]');
      if (nameEl) nameEl.textContent = windowDisplayName(win);
      if (detailEl) detailEl.textContent = windowDeliveryPhrase(win);
      if (priceEl) priceEl.textContent = index === 0 ? this.sameDayPrice : '';
    });

    if (this.sameDayCheckRow) this.sameDayCheckRow.hidden = resolved;
    if (this.noteEl) this.noteEl.hidden = !showWindows;
  }

  fetchAvailability(postcode, suburb) {
    if (!this.availabilityUrl) return;
    let url;
    try {
      url = new URL(this.availabilityUrl, window.location.origin);
    } catch (_e) {
      return;
    }
    url.searchParams.set('postcode', postcode);
    if (suburb) url.searchParams.set('suburb', suburb);
    if (this.productId) url.searchParams.set('productId', this.productId);

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => controller && controller.abort(), FETCH_TIMEOUT_MS);
    const opts = { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } };
    if (controller) opts.signal = controller.signal;

    fetch(url.toString(), opts)
      .then((res) => {
        if (!res.ok) throw new Error('bad status');
        return res.json();
      })
      .then((data) => {
        clearTimeout(timer);
        const store = sharedStore();
        if (this.productId) store.availabilityByProduct[this.productId] = data || null;
        writeStorage(postcode, suburb, deriveState(data));
        this.renderHomeDelivery(data || null);
      })
      .catch(() => clearTimeout(timer));
  }
}

/** Derive the cached availability state flag from a raw response. */
function deriveState(data) {
  let state = String((data && data.state) || '').toLowerCase();
  if (!state && data && data.enabled === false) state = 'not_serviced';
  if (!state && data && Array.isArray(data.windows)) state = data.windows.length ? 'ok' : 'empty';
  return state;
}

if (!customElements.get('shipping-returns-panel')) {
  customElements.define('shipping-returns-panel', ShippingReturnsPanel);
}
